import { Box, Icon, IconButton, Stack, styled, Typography } from '@mui/material';
import {
  ConductAggregatedRow,
  ConductConnotation,
  ConductEntry,
  ConductTypeRepresentation,
  ConductVisibility,
  GetConductEntriesQuerySort,
  SORT_DIRECTION,
  useGetConductTypesForSchoolQuery,
} from '@schooly/api';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { DATE_FORMAT_SHORT } from '@schooly/constants';
import {
  ArchiveIcon,
  DropdownIcon,
  EditIcon,
  GridCell,
  GridHead,
  GridRow,
  GridRowProps,
  SortableCell,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { getUserFullName } from '@schooly/utils/get-user-full-name';
import { format } from 'date-fns';
import { FC, ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ConductVisibilityButton } from './ConductVisibilityButton';

export const PAGE_SIZE = 30;
export const SKELETON_COLS = 8;

type ConductEntriesHeaderProps = {
  sort?: GetConductEntriesQuerySort;
  onChangeSort: (v: GetConductEntriesQuerySort) => void;
  rightIcon?: ReactNode;
  isGrouped?: boolean;
};

export const ConductEntriesHeader: FC<ConductEntriesHeaderProps> = ({
  onChangeSort,
  sort,
  rightIcon,
  isGrouped,
}) => {
  const { formatMessage } = useIntl();
  const handleSort = (columnTextId: GetConductEntriesQuerySort['columnTextId']) => () => {
    onChangeSort({
      columnTextId,
      direction:
        columnTextId === sort?.columnTextId
          ? sort.direction === SORT_DIRECTION.ASC
            ? SORT_DIRECTION.DESC
            : SORT_DIRECTION.ASC
          : SORT_DIRECTION.ASC,
    });
  };

  return (
    <GridHead borderBottom>
      <SortableCell
        label={formatMessage({ id: 'assessments-Name' })}
        sort={sort}
        width="50%"
        columnTextId="student"
        onChangeSort={handleSort}
        pl={2}
      />
      {isGrouped ? (
        <GridCell width="70px">{formatMessage({ id: 'conduct-Date' })}</GridCell>
      ) : (
        <SortableCell
          width="70px"
          label={formatMessage({ id: 'conduct-Date' })}
          columnTextId="date"
          sort={sort}
          onChangeSort={handleSort}
        />
      )}
      <GridCell width="70px">{formatMessage({ id: 'conduct-visibility' })}</GridCell>
      {isGrouped ? (
        <GridCell
          width="110px"
          sx={(theme) => ({
            [theme.breakpoints.only('md')]: {
              width: 90,
            },
          })}
        >
          {formatMessage({ id: 'conduct-Type' })}
        </GridCell>
      ) : (
        <SortableCell
          width="110px"
          label={formatMessage({ id: 'conduct-Type' })}
          columnTextId="conduct_type"
          sort={sort}
          onChangeSort={handleSort}
          sx={(theme) => ({
            [theme.breakpoints.only('md')]: {
              width: 90,
            },
          })}
        />
      )}
      <GridCell
        width="110px"
        sx={(theme) => ({
          [theme.breakpoints.only('lg')]: {
            width: 90,
          },
          [theme.breakpoints.only('md')]: {
            width: 70,
          },
          [theme.breakpoints.down('md')]: {
            width: 90,
          },
        })}
      >
        {formatMessage({ id: 'conduct-TitleValue' })}
      </GridCell>
      <GridCell width="50%">{formatMessage({ id: 'conduct-Detail' })}</GridCell>
      <GridCell width="40px" />
      <GridCell
        width="120px"
        sx={(theme) => ({
          [theme.breakpoints.only('lg')]: {
            width: 110,
          },
          [theme.breakpoints.only('md')]: {
            width: 90,
          },
          [theme.breakpoints.down('md')]: {
            width: 110,
          },
        })}
      >
        {formatMessage({ id: 'conduct-Teacher' })}
      </GridCell>
      <GridCell width="40px" />
      <GridCell width="40px" pr={1} right>
        {rightIcon}
      </GridCell>
    </GridHead>
  );
};

type ConductStudentGroupRowProps = {
  row: ConductAggregatedRow;
  isExpanded: boolean;
  hoverIdStudentId: string | null;
  isLast?: boolean;
  onExpand: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

export const ConductStudentGroupRow: FC<ConductStudentGroupRowProps> = ({
  row,
  isExpanded,
  hoverIdStudentId,
  isLast,
  onExpand,
  onMouseEnter,
  onMouseLeave,
}) => {
  const studentHoverId = row.value;

  return (
    <GridRow
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onExpand}
      sx={{ cursor: 'pointer' }}
    >
      <GridCell
        sx={{
          position: 'relative',
          '&&': {
            backgroundColor: hoverIdStudentId === studentHoverId ? 'background.default' : undefined,
          },
          '&&&&&': { borderBottomColor: isExpanded && !isLast ? 'transparent' : undefined },
        }}
      >
        <AvatarAuth
          withAvatarPreview
          user={row.meta}
          sx={(theme) => ({
            position: 'absolute',
            left: theme.spacing(1),
            marginTop: theme.spacing(-0.5),
          })}
        />
        <TypographyWithOverflowHint ml={5} variant="h3" color="common.grey2" noWrap>
          {row.name}
        </TypographyWithOverflowHint>
      </GridCell>

      <GridCell />
      <GridCell />

      <GridCell colSpan={4}>
        <Stack direction="row" gap={2} flexWrap="wrap">
          {row.type_counts.map((tc) => (
            <ConnotationCellContent
              key={tc.id}
              type={tc.connotation}
              label={`${tc.count} ${tc.name}`}
            />
          ))}
        </Stack>
      </GridCell>

      <GridCell />
      <GridCell />

      <GridCell
        right
        sx={{
          pr: 1,
          ...(isExpanded && !isLast
            ? { '&&&&&': { borderBottomColor: 'transparent' } }
            : undefined),
        }}
      >
        <IconButton
          inverse
          sx={{ transform: isExpanded ? 'rotate(180deg)' : undefined }}
          onClick={(event) => {
            event.stopPropagation();
            onExpand();
          }}
        >
          <DropdownIcon />
        </IconButton>
      </GridCell>
    </GridRow>
  );
};

export type ConductEntryRowProps = {
  entry: ConductEntry;
  commentsCellContent?: ReactNode;
  isLast?: boolean;
  isExpanded?: boolean;
  isEditable?: boolean;
  schoolId: string;
  hideStudentName?: boolean;
  hoverIdStudentId: string | null;
  onExpand?: () => void;
  onClick?: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
} & GridRowProps;

export const ConductEntryRow: FC<ConductEntryRowProps> = ({
  schoolId,
  entry,
  isExpanded,
  hoverIdStudentId,
  isEditable,
  isLast,
  hideStudentName,
  commentsCellContent,
  onExpand,
  onClick,
  ...rowProps
}) => {
  const studentHoverId = entry.student.relation_id;
  const { data: types } = useGetConductTypesForSchoolQuery(schoolId);

  const canShowVisibilityIcon = entry.visibility === ConductVisibility.PUBLISHED;

  let titleOrValue: ReactNode;

  const conductType = types?.find((type) => type.id === entry.conduct_type.id);

  const studentName = getUserFullName(entry.student);
  const staffName = getUserFullName(entry.staff);

  if (conductType?.type === ConductTypeRepresentation.NUMBER) {
    titleOrValue = entry.value;
  } else {
    titleOrValue = entry.title;
  }

  const archived = useMemo(() => {
    if (!conductType?.type || conductType.type !== ConductTypeRepresentation.SELECT) return;
    return conductType?.select_type_options?.find((o) => o.label === entry.title)?.archived;
  }, [conductType, entry.title]);

  return (
    <GridRow
      {...rowProps}
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          '.edit-icon': {
            opacity: 1,
          },
        },
      }}
    >
      {/* Student name */}
      <GridCell
        sx={{
          position: 'relative',
          '&&': {
            backgroundColor: hoverIdStudentId === studentHoverId ? 'background.default' : undefined,
          },
          '&&&&&': { borderBottomColor: isExpanded && !isLast ? 'transparent' : undefined },
        }}
      >
        {!hideStudentName && (
          <>
            <AvatarAuth
              withAvatarPreview
              user={entry.student}
              sx={(theme) => ({
                position: 'absolute',
                left: theme.spacing(1),
                marginTop: theme.spacing(-0.5),
              })}
            />
            <TypographyWithOverflowHint ml={5} variant="h3" color="common.grey2" noWrap>
              {studentName}
            </TypographyWithOverflowHint>
          </>
        )}
      </GridCell>

      {/* Date */}
      <GridCell>
        <Typography>{format(newDateTimezoneOffset(entry.date), DATE_FORMAT_SHORT)}</Typography>
      </GridCell>

      <GridCell>
        {canShowVisibilityIcon && (
          <ConductVisibilityButton
            tooltipTitle={<FormattedMessage id="conduct-parents-visible" />}
            selected
            nowrap
          />
        )}
      </GridCell>

      {/* Conduct type */}
      <GridCell>
        <ConnotationCellContent
          type={entry.conduct_type.connotation}
          label={entry.conduct_type.name}
        />
      </GridCell>

      {/* Title / Value */}
      <GridCell>
        <Stack direction="row" gap={0.5} alignItems="center">
          {archived && (
            <Icon>
              <ArchiveIcon />
            </Icon>
          )}
          <Typography>{titleOrValue ?? '-'}</Typography>
        </Stack>
      </GridCell>

      {/* Detail */}
      <GridCell>
        <TypographyWithOverflowHint>{entry.details || '—'}</TypographyWithOverflowHint>
      </GridCell>

      {/* Comment */}
      <GridCell>{commentsCellContent}</GridCell>

      {/* Teacher */}
      <GridCell>
        <TypographyWithOverflowHint>{staffName}</TypographyWithOverflowHint>
      </GridCell>

      {/* Edit icon */}
      <GridCell>
        {isEditable && (
          <Icon
            className="edit-icon"
            sx={(theme) => ({
              opacity: 0,
              transition: theme.transitions.create('opacity', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shortest,
              }),
            })}
          >
            <EditIcon />
          </Icon>
        )}
      </GridCell>

      {/* Dropdown icon */}
      <GridCell
        right
        sx={{
          pr: 1,
          ...(isExpanded && !isLast
            ? { '&&&&&': { borderBottomColor: 'transparent' } }
            : undefined),
        }}
      >
        {onExpand && (
          <IconButton
            inverse
            sx={{ transform: isExpanded ? 'rotate(180deg)' : undefined }}
            onClick={(event) => {
              event.stopPropagation();
              onExpand();
            }}
          >
            <DropdownIcon />
          </IconButton>
        )}
      </GridCell>
    </GridRow>
  );
};

type ConnotationCellContentProps = {
  type: ConductConnotation;
  label: ReactNode;
};
export const ConnotationCellContent: FC<ConnotationCellContentProps> = ({ type, label }) => {
  return (
    <Stack direction="row" alignItems="baseline" gap={1}>
      <ConductConnotationIndicator connotation={type} />
      <TypographyWithOverflowHint>{label}</TypographyWithOverflowHint>
    </Stack>
  );
};

export interface ConductConnotationIndicatorProps {
  connotation: ConductConnotation;
}

export const ConductConnotationIndicator = styled(Box)<ConductConnotationIndicatorProps>(
  ({ theme, connotation }) => ({
    flex: '0 0 auto',
    width: theme.spacing(),
    height: theme.spacing(),
    borderRadius: '50%',
    background:
      connotation === ConductConnotation.POSITIVE
        ? theme.palette.success.main
        : theme.palette.error.main,
  }),
);
